import { Link } from "react-router-dom";
import { MapPin, Phone, Flag, Star } from "lucide-react";
import { BUSINESS } from "@/lib/constants";
import { CITY_PAGE_LIST } from "@/lib/cityPages";

const sitemap = [
  {
    heading: "Sell",
    links: [
      { to: "/sell-your-car", label: "Sell Your Car" },
      { to: "/how-it-works", label: "How It Works" },
      { to: "/service-areas", label: "Service Areas" },
      { to: "/faq", label: "FAQ" },
      { to: "/compare", label: "vs CarMax & Carvana" },
    ],
  },
  {
    heading: "Local",
    links: CITY_PAGE_LIST.map((c) => ({ to: `/sell-my-car/${c.slug}`, label: `Sell a car in ${c.name}` })),
  },
  {
    heading: "Company",
    links: [
      { to: "/about", label: "About" },
      { to: "/contact", label: "Contact" },
    ],
  },
  {
    heading: "Legal",
    links: [
      { to: "/privacy", label: "Privacy Policy" },
      { to: "/terms", label: "Terms of Service" },
    ],
  },
];

export default function Footer() {
  return (
    <footer className="relative mt-24 bg-[#0B1220] text-slate-300" data-testid="site-footer">
      <div className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-brand-blue/60 to-transparent" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5 space-y-5">
            <div className="inline-flex items-center gap-2">
              <svg width="34" height="34" viewBox="0 0 40 40" fill="none" aria-hidden>
                <rect width="40" height="40" rx="9" fill="#0052FF"/>
                <path d="M9 24c0-1.4 1.1-2.5 2.5-2.5h17c1.4 0 2.5 1.1 2.5 2.5v2.5c0 .6-.4 1-1 1H10c-.6 0-1-.4-1-1V24z" fill="white"/>
                <path d="M12 21.5l2.2-5.4c.3-.7 1-1.1 1.7-1.1h8.2c.7 0 1.4.4 1.7 1.1l2.2 5.4H12z" fill="white" opacity=".85"/>
                <circle cx="14" cy="28" r="1.8" fill="#0F172A"/>
                <circle cx="26" cy="28" r="1.8" fill="#0F172A"/>
              </svg>
              <div className="flex flex-col leading-none">
                <span className="font-display text-[22px] font-extrabold tracking-tight text-white">Carfornia <span className="text-brand-blue">Buyers</span></span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-bold -mt-0.5">Hayward, California</span>
              </div>
            </div>
            <p className="text-base text-slate-400 leading-relaxed max-w-md">
              A veteran-owned car-buying business based in Hayward, California. Tell us about your car, we inspect it, and you get a real written offer — no pressure.
            </p>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-white/5 text-xs font-bold uppercase tracking-[0.18em] text-slate-200" data-testid="footer-veteran-badge">
              <Flag className="w-3.5 h-3.5 text-brand-orange" /> Veteran-owned &amp; operated
            </div>
            <ul className="space-y-2 text-sm text-slate-400">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-brand-blue" /><a href={BUSINESS.phoneHref} data-testid="footer-phone" className="hover:text-white">{BUSINESS.phone}</a></li>
              <li className="flex items-center gap-2"><MapPin className="w-4 h-4 text-brand-blue" /><span data-testid="footer-address">{BUSINESS.address}</span></li>
              <li className="flex items-center gap-2"><Star className="w-4 h-4 text-[#FF1A1A]" /><a href={BUSINESS.yelpUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-yelp" className="hover:text-white">Read our reviews on Yelp</a></li>
            </ul>
          </div>

          <div className="lg:col-span-7 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {sitemap.map((col) => (
              <div key={col.heading}>
                <h4 className="text-xs uppercase tracking-[0.2em] text-brand-blue font-bold mb-4">{col.heading}</h4>
                <ul className="space-y-3">
                  {col.links.map((l) => (
                    <li key={l.to}>
                      <Link to={l.to} className="text-sm text-slate-300 hover:text-white transition-colors">{l.label}</Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="border-t border-white/10 mt-14 pt-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-3 text-xs text-slate-500">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} {BUSINESS.name}. All rights reserved.</p>
          <p>Online submissions are offer requests, not purchase offers. All offers are made in writing after inspection.</p>
        </div>
      </div>
    </footer>
  );
}
